/* Normalised search keys stored on every customer document.
 *
 * Firestore can only answer a prefix range over a field exactly as that field
 * is stored, which left the customer picker with three holes:
 *
 *   - `userId` is a *string* (see the note above getUsersPage in
 *     firebase.utils.js), so a range for "2" cannot reach "02" even though
 *     they are the same customer number to anyone typing it.
 *   - `displayName` is matched in raw byte order, so "fm" never reaches "FM".
 *   - `mobileNo` is written however the customer app got it — "01712…",
 *     "+8801712…", "+880 1712…" — so no single spelling finds all of them.
 *
 * So each value is also stored canonicalised, where a range query can reach
 * it, and the *typed query* is canonicalised through the very same function.
 * Canonicalising both sides is what makes 2 ≡ 02 and 01712… ≡ +8801712…; it
 * is the step lotSearchKeys.js deliberately left undone for lot numbers.
 *
 * Written as CommonJS on purpose: this is imported by the app *and* required
 * by scripts/backfill-user-search-keys.js, which is plain Node and cannot
 * load an ES module. One copy means the backfill and the app can never
 * disagree about a customer's keys — which they would eventually, if this
 * were duplicated.
 *
 * Customers are created by the customer app, not by this panel, so a new
 * registration arrives with none of these fields. Two things cover that and
 * neither needs the other app changed: searchUsers also queries the raw
 * fields with case/padding variants, and stampUserSearchKeys writes the keys
 * back for any doc read without them.
 *
 * Unit tests: src/utils/userSearchKeys.test.js
 */

// The field names live here rather than as literals at each use, so app,
// script and tests cannot drift apart on them. Exported one by one as well as
// grouped: webpack's CommonJS interop resolves a flat string constant across
// an `import { X }` reliably, the way LOT_DIGITS_FIELD already is, where it
// does not resolve a key reached through an exported object.
const USER_ID_DIGITS_FIELD = "userIdDigits";
const NAME_LOWER_FIELD = "displayNameLower";
const MOBILE_DIGITS_FIELD = "mobileNoDigits";
const EMAIL_LOWER_FIELD = "emailLower";

const USER_SEARCH_FIELDS = {
  idDigits: USER_ID_DIGITS_FIELD,
  nameLower: NAME_LOWER_FIELD,
  mobileDigits: MOBILE_DIGITS_FIELD,
  emailLower: EMAIL_LOWER_FIELD,
};

const digitsOnly = (value) => String(value == null ? "" : value).replace(/\D/g, "");

/**
 * A customer number with its leading zeros dropped.
 *
 *   "2"   -> "2"        "02"    -> "2"
 *   "021" -> "21"       "0"     -> "0"
 *   "abc" -> ""         ""      -> ""
 *
 * Only all-digit ids get a key. Anything else stores "" and stays reachable
 * through the raw `userId` range, the same way it always was — a stored empty
 * string means "canonicalised, nothing to match", which no query can reach
 * because a search string is never empty.
 */
const userIdDigits = (userId) => {
  const trimmed = String(userId == null ? "" : userId).trim();
  if (!/^\d+$/.test(trimmed)) return "";
  const stripped = trimmed.replace(/^0+/, "");
  // "0" and "000" are the same customer number, and it is not the empty
  // string — that would read as "no key" above.
  return stripped === "" ? "0" : stripped;
};

/**
 * The digit form of a typed query, or "" when the query is not purely digits.
 * This is the branch switch in searchUsers: an empty result means the numeric
 * ranges are skipped entirely for a query like "fm".
 */
const userIdDigitsQuery = (query) => userIdDigits(query);

/** Case- and whitespace-normalised name. "  Kazi   Nur " -> "kazi nur". */
const nameLower = (displayName) =>
  String(displayName == null ? "" : displayName)
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

/**
 * A phone number reduced to the part that identifies the subscriber.
 *
 *   "01712345678"     -> "1712345678"
 *   "+8801712345678"  -> "1712345678"
 *   "+880 1712-345678"-> "1712345678"
 *   "008801712345678" -> "1712345678"
 *
 * The 880 is dropped before the second zero-strip because a Bangladeshi
 * mobile is 01… — after its leading zero goes it starts with 1, never 880 —
 * so a leading 880 is always the country code and never part of the number.
 */
const mobileDigits = (mobileNo) => {
  let digits = digitsOnly(mobileNo).replace(/^0+/, "");
  if (digits.startsWith("880")) digits = digits.slice(3);
  return digits.replace(/^0+/, "");
};

const emailLower = (email) =>
  String(email == null ? "" : email)
    .trim()
    .toLowerCase();

/** Every key a customer document should carry, derived from the document.
 *
 * Built by assignment rather than with computed keys (`{ [FIELD]: value }`).
 * Babel lowers a computed key through its `_defineProperty` helper, which it
 * adds as an ES `import` — and that one injected import is enough for webpack
 * to treat this whole file as an ES module, at which point the
 * `module.exports` below assigns to a read-only stub and throws on load. The
 * field names still come from the constants, so there is still one place they
 * are written down. See lotSearchKeys.js, which stays plain CommonJS for the
 * same reason without having to think about it.
 */
const userSearchKeys = (user) => {
  const keys = {};
  keys[USER_ID_DIGITS_FIELD] = userIdDigits(user && user.userId);
  keys[NAME_LOWER_FIELD] = nameLower(user && user.displayName);
  keys[MOBILE_DIGITS_FIELD] = mobileDigits(user && user.mobileNo);
  keys[EMAIL_LOWER_FIELD] = emailLower(user && user.email);
  return keys;
};

/**
 * The keys that are missing or stale on a document, or null when it is
 * already correct. Both the backfill and the stamp-on-read path use this, so
 * a document that needs nothing costs no write in either.
 */
const staleUserSearchKeys = (user) => {
  const wanted = userSearchKeys(user);
  const changed = {};
  Object.keys(wanted).forEach((field) => {
    if (user && user[field] === wanted[field]) return;
    changed[field] = wanted[field];
  });
  return Object.keys(changed).length ? changed : null;
};

module.exports = {
  USER_ID_DIGITS_FIELD,
  NAME_LOWER_FIELD,
  MOBILE_DIGITS_FIELD,
  EMAIL_LOWER_FIELD,
  USER_SEARCH_FIELDS,
  userIdDigits,
  userIdDigitsQuery,
  nameLower,
  mobileDigits,
  emailLower,
  userSearchKeys,
  staleUserSearchKeys,
};
